import { Box } from '../../Box';
import { forwardRef } from 'react';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { resolveInputSize } from '../../TextInput/TextInputStyles';
import { clsx } from 'clsx';
import { jsx as _jsx } from "react/jsx-runtime";
const resolveBorderColor = (disabled, hasError, theme) => {
  if (disabled) {
    return `1px solid ${theme.tokens.SemColorBorderInteractiveDisabled}`;
  }
  if (hasError) {
    return `1px solid ${theme.tokens.SemColorBorderError}`;
  }
  return `1px solid ${theme.tokens.SemColorBorderDefault}`;
};
const resolvePadding = size => {
  const map = {
    small: '8px',
    sm: '8px',
    medium: '12px',
    md: '12px',
    large: '16px',
    lg: '16px'
  };
  return map[size || 'medium'];
};
/**
 * Resolves the style object for a TextInput component based on the provided theme, size, disabled, and multiple properties.
 *
 * @param {InterstateTheme} theme - The theme object containing the token values.
 * @param {TextInputSize} size - The size of the TextInput.
 * @param {boolean} multiple - Indicates whether the TextInput allows multiple values.
 * @param {boolean} disabled - Indicates whether the TextInput is disabled.
 * @param hasError
 * @returns {Object} - The resolved style object.
 */
const resolveStyle = (theme, size, multiple, disabled, hasError) => ({
  width: '100%',
  borderRadius: '4px',
  display: 'flex',
  paddingLeft: resolvePadding(size),
  alignItems: 'flex-start',
  gap: '8px',
  alignSelf: 'stretch',
  border: resolveBorderColor(disabled, hasError, theme),
  background: !disabled ? theme.tokens.SemColorSurfaceDefault : theme.tokens.SemColorSurfaceInteractiveDisabled,
  minHeight: resolveInputSize(theme, size),
  // height: collapse ? resolveInputSize(theme, size) : 'none',
  ':focus-within': {
    border: `1px solid ${theme.tokens.ComponentTextInputBorderFocusColor}`,
    boxShadow: theme.tokens.SemRingFocus
  },
  ':active': {
    border: !disabled ? `1px solid ${theme.tokens.ComponentTextInputBorderFocusColor}` : `1px solid ${theme.tokens.SemColorBorderInteractiveDisabled}`,
    boxShadow: !disabled ? theme.tokens.SemRingFocus : 'none'
  }
});

/**
 * ComboBoxInputChipsContainer component
 *
 * This component is a container for input and chips in a combo box.
 *
 * @param {ComboBoxInputsProps} props - The component props.
 * @param {React.Ref} ref - Ref object to access the underlying DOM element.
 * @returns {React.Element} The rendered ComboBoxInputChipsContainer component.
 *
 * @example
 * // Usage
 * const ref = useRef();
 * <ComboBoxInputChipsContainer
 *   size="small"
 *   disabled={false}
 *   multiple={true}
 *   ref={ref}
 * >
 *   {/* Content goes here */
export const ComboBoxInputChipsContainer = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    size,
    disabled,
    multiple,
    hasError,
    onClick
  } = props;
  const theme = useInterstateTheme();
  const comboBoxInputStyle = resolveStyle(theme, size, multiple, Boolean(disabled), hasError);
  return /*#__PURE__*/_jsx(Box, {
    sx: comboBoxInputStyle,
    className: clsx(['ids-combo-box-input-and-chips', disabled ? 'ids-combo-box-disabled' : undefined]),
    ref: ref,
    onClick: onClick,
    children: children
  });
});
ComboBoxInputChipsContainer.displayName = 'ComboInputs';